import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Container,
  Grid2,
  IconButton,
  Paper,
} from "@mui/material";
import {
  determineHeaderTimes,
  generateTimeSlotOptions,
  hexToRgba,
  notifErr,
} from "../../components/Functions";
import handleAxiosError from "../../components/ErrorHandler";
import { Loading } from "../../components/Loading";
import { TrainingArea } from "../../model/entity/TrainingArea";
import { useNavigate } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  ChangeCircle,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  ReportGmailerrorred,
} from "@mui/icons-material";
import { CourseInstance } from "../../model/entity/CourseInstance";
import { getFetcher } from "../../components/Fetchers";
import AttendanceDialog from "../../components/AttendanceDialog";

const Attendance = () => {
  const navigate = useNavigate();
  const [headerTimes, setHeaderTimes] = useState<string[] | null>(null);
  const [instances, setInstances] = useState<CourseInstance[]>([]);
  const [areas, setAreas] = useState<TrainingArea[] | null>(null); // todo fetch areas, only loading icon is showing now
  const [selectedInstance, setSelectedInstance] =
    useState<CourseInstance | null>(null);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [openAttendanceDialog, setOpenAttendanceDialog] = useState(false);

  const [hoveredInstanceId, setHoveredInstanceId] = useState<number | null>(
    null
  );
  const handleHoveredInstanceId = (slotId: number | null) => {
    if (slotId !== hoveredInstanceId) setHoveredInstanceId(slotId);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const areasRes = await getFetcher(
          `${process.env.REACT_APP_API_URL}/training-areas/sorted-by-code`
        );
        setAreas(areasRes.data);
      } catch (err) {
        notifErr(handleAxiosError(err));
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!selectedDate || openAttendanceDialog) return;

    const fetchData = async () => {
      try {
        const config = { date: selectedDate.format("YYYY-MM-DD") };
        const instancesRes = await getFetcher(
          `${process.env.REACT_APP_API_URL}/course-instances/all-active-by-date`,
          config
        );
        setInstances(instancesRes.data);
        const { from, to } = determineHeaderTimes(instancesRes.data);
        setHeaderTimes(generateTimeSlotOptions(from, to, to !== 24));
      } catch (err) {
        notifErr(handleAxiosError(err));
      }
    };
    fetchData();
  }, [selectedDate, openAttendanceDialog]);

  const handleShiftDay = (days: number) => {
    if (!selectedDate) return;
    const newSelectedDate = selectedDate.add(days, "d");
    setSelectedDate(newSelectedDate);
  };

  const handleSelectedInstance = (instance: CourseInstance) => {
    setSelectedInstance(instance);
    setOpenAttendanceDialog(true);
  };

  const handleAttendanceDialogCancel = () => {
    setOpenAttendanceDialog(false);
    setTimeout(() => {
      setSelectedInstance(null);
    }, 100);
  };

  const findInstanceForCell = (area: TrainingArea, time: string) => {
    const matchingCourse = instances.find((instance) => {
      const instanceStart = instance.startTime.substring(0, 5); // the times came in format "HH:MM"
      const instanceEnd = instance.endTime.substring(0, 5);

      const isWithinTime =
        time >= instanceStart &&
        (time < instanceEnd || instanceEnd === "00:00");
      const isAtThisArea = instance.trainingAreaIds.includes(area.id!);

      return isWithinTime && isAtThisArea;
    });

    return matchingCourse;
  };

  if (!instances || !areas || !headerTimes) return <Loading />;

  const getCellSpan = (instance: CourseInstance) => {
    const startTimeIndex = headerTimes.indexOf(
      instance.startTime.substring(0, 5)
    );
    const endTime = instance.endTime.substring(0, 5);
    const endTimeIndex =
      endTime === "00:00"
        ? headerTimes.length
        : headerTimes.indexOf(instance.endTime.substring(0, 5));
    return endTimeIndex - startTimeIndex;
  };

  return (
    <Container
      className="scheduleContainer"
      maxWidth={false}
      sx={{ userSelect: "none" }}
    >
      {selectedInstance && (
        <AttendanceDialog
          open={openAttendanceDialog}
          selectedInstance={selectedInstance!}
          handleCancel={handleAttendanceDialogCancel}
        />
      )}
      <TableContainer
        className="scheduleTableContainer"
        onMouseLeave={() => handleHoveredInstanceId(null)}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mb: 0.5,
            position: "sticky",
            left: 0,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: "fit-content",
              display: "flex",
              alignItems: "center",
              border: "1px solid var(--gradientEnd)",
              borderRadius: "2rem",
            }}
          >
            <IconButton
              sx={{ color: "var(--gradientEnd)" }}
              onClick={() => handleShiftDay(-7)}
            >
              <KeyboardDoubleArrowLeft />
            </IconButton>
            <IconButton
              sx={{ color: "var(--gradientEnd)" }}
              onClick={() => handleShiftDay(-1)}
            >
              <KeyboardArrowLeft />
            </IconButton>
            <Typography
              id={"dateLabel"}
              variant="h5"
              sx={{
                cursor: "pointer",
                color: "var(--gradientEnd)",
                mx: ".5rem",
                fontSize: "1.3rem",
              }}
              onClick={() => setOpenDatePicker(true)}
            >
              {selectedDate?.format("DD.MM.YYYY")}
            </Typography>
            <IconButton
              sx={{ color: "var(--gradientEnd)" }}
              onClick={() => handleShiftDay(1)}
            >
              <KeyboardArrowRight />
            </IconButton>
            <IconButton
              sx={{ color: "var(--gradientEnd)" }}
              onClick={() => handleShiftDay(7)}
            >
              <KeyboardDoubleArrowRight />
            </IconButton>
          </Paper>
        </Box>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            open={openDatePicker}
            value={selectedDate}
            onClose={() => setOpenDatePicker(false)}
            onChange={(newValue) => setSelectedDate(newValue)}
            slotProps={{
              textField: {
                sx: {
                  width: 0,
                  height: 0,
                  opacity: 0,
                  overflow: "hidden",
                  position: "absolute",
                },
                inputRef: (input) => {
                  if (input && openDatePicker) {
                    input.focus();
                  }
                },
              },
              popper: {
                anchorEl: document.getElementById("dateLabel"),
                placement: "bottom",
              },
            }}
          />
        </LocalizationProvider>
        <Table sx={{ mt: ".75rem" }}>
          <TableHead>
            <TableRow
              key="timeHeader"
              onMouseEnter={() => handleHoveredInstanceId(null)}
            >
              <TableCell
                key={"cancel-mode-cell"}
                className="scheduleHeaderTimeCell"
              />
              {headerTimes.map((time, idx) => (
                <TableCell key={`${time}`} className="scheduleHeaderTimeCell">
                  {time}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {areas.map((area, areaIndex) => (
              <TableRow key={areaIndex}>
                <TableCell
                  key={area.id}
                  className="scheduleTrainingAreaCell"
                  onClick={() =>
                    navigate(`/schedule/training-areas/edit/${area.id}`)
                  }
                  onMouseEnter={() => handleHoveredInstanceId(null)}
                >
                  {area.code}
                </TableCell>
                {headerTimes.map((time, timeIndex) => {
                  const instance = findInstanceForCell(area, time);
                  const cellKey = `${area.id}-${time}`;
                  if (timeIndex > 0) {
                    const prevTime = headerTimes[timeIndex - 1];
                    const prevInstance = findInstanceForCell(area, prevTime);
                    if (prevInstance && prevInstance.id === instance?.id) {
                      return null;
                    }
                  }

                  if (instance) {
                    const span = getCellSpan(instance);
                    const canceled = instance.canceled;
                    const canceledOp = 0.4;

                    let above = false;
                    let below = false;
                    let objectAbove =
                      areaIndex !== 0
                        ? findInstanceForCell(areas[areaIndex - 1], time)
                        : null;
                    let objectBelow =
                      areaIndex + 1 !== areas.length
                        ? findInstanceForCell(areas[areaIndex + 1], time)
                        : null;
                    if (objectAbove) above = objectAbove.id === instance.id;
                    if (objectBelow) below = objectBelow.id === instance.id;

                    const isTop = !above && below;
                    const isBottom = above && !below;
                    const isOnlyOne = !above && !below;

                    return (
                      <TableCell
                        key={cellKey}
                        className="scheduleCourseCell"
                        colSpan={span}
                        sx={{
                          borderTop:
                            isTop || isOnlyOne ? "1px solid white" : "none",
                          borderBottom:
                            isBottom || isOnlyOne ? "1px solid white" : "none",
                          borderLeft: "1px solid white",
                          borderRight: "1px solid white",
                          opacity: hoveredInstanceId === instance.id ? 0.7 : 1,
                          background: canceled
                            ? `${hexToRgba(instance.course.color, canceledOp)}`
                            : instance.course.color,
                        }}
                        onClick={() => {
                          handleSelectedInstance(instance);
                        }}
                        onMouseMove={() => handleHoveredInstanceId(instance.id)}
                      >
                        <Box className="scheduleCourseContainer">
                          {!above && (
                            <Typography
                              className="courseContainerText"
                              variant="caption"
                              sx={{
                                fontSize: "0.8rem",
                                opacity: canceled ? canceledOp : 1,
                              }}
                            >
                              <b>{instance.course.name}</b> (
                              {instance.course.memberInCourses.length}/
                              {instance.course.capacity})
                            </Typography>
                          )}
                          {instance.canceled && (isTop || isOnlyOne) && (
                            <Typography className="scheduleCanceledCellLabel">
                              Zrušeno
                            </Typography>
                          )}
                          {!below && (
                            <Grid2
                              sx={{
                                opacity: canceled ? canceledOp : 1,
                                marginTop: "auto",
                              }}
                            >
                              {instance.course.coachLeadsCourses.length > 0 ? (
                                instance.course.coachLeadsCourses.map(
                                  (c, index) => (
                                    <Typography
                                      key={c.coach.id}
                                      className="courseContainerText"
                                      variant="caption"
                                    >
                                      {c.coach.nickname}
                                      {index !==
                                      instance.course.coachLeadsCourses.length -
                                        1
                                        ? ", "
                                        : ""}
                                    </Typography>
                                  )
                                )
                              ) : (
                                <Typography className="courseContainerText" variant="caption">
                                  bez trenéra
                                </Typography>
                              )}
                              {instance.coachIds.length === 0 &&
                                instance.course.coachLeadsCourses.length !== 0 ? (
                                  <ReportGmailerrorred
                                    sx={{
                                      color: "var(--redNotOK)",
                                      fontSize: "1.1rem",
                                      ml: "0.1rem",
                                    }}
                                  />
                                ) : (
                                  (instance.coachIds.length !==
                                    instance.course.coachLeadsCourses.length ||
                                    instance.coachIds.some((id) =>
                                      instance.course.coachLeadsCourses.every(
                                        (clc) => clc.coach.id !== id
                                      )
                                    )) && (
                                    <ChangeCircle
                                      sx={{
                                        color: "var(--gradientStart)",
                                        fontSize: "1.1rem",
                                        ml: "0.1rem",
                                      }}
                                    />
                                  )
                                )}
                            </Grid2>
                          )}
                        </Box>
                      </TableCell>
                    );
                  }

                  return (
                    <TableCell
                      key={cellKey}
                      className="scheduleEmptyCell"
                      onMouseEnter={() => handleHoveredInstanceId(null)}
                    />
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default Attendance;
